/**
 * AS COLETAS, UMA POR LINHA (20/10, "reimaginar tudo"): no lugar da tabela de
 * onze colunas, cada coleta é uma linha — o estado numa pílula, a fonte, quando
 * e quanto durou, e os números que importam lado a lado. As três causas de um
 * `parcial` (páginas perdidas, campos ausentes, assinatura da grade) só
 * aparecem quando existem, e em cor de atenção; o erro vem por extenso embaixo.
 *
 * `parcial` não é falha: o run terminou e viu menos do que existia. Os quatro
 * estados aparecem como o banco os guarda — sem tradução.
 *
 * AS PEÇAS DO DESIGN SYSTEM (20/10): a lista, a linha, a pílula e o aviso saem
 * de `src/ds`; o tom da pílula sai do estado (`TOM`). Em `gestao.module.css`
 * ficaram os alertas, o erro e os três números da coleta.
 */
import type { Rodada } from "../api/cliente";
import { Aviso, ItemDeLista, Lista, Pilula, type TomDeEstado } from "../ds";
import css from "./gestao.module.css";

/** Segundos → "1 h 04 min" · "12 min" · "48 s". */
export function duracao(de: string, ate?: string | null) {
  const s = Math.max(0, ((ate ? Date.parse(ate) : Date.now()) - Date.parse(de)) / 1000);
  if (s < 90) return `${Math.round(s)} s`;
  const min = Math.floor(s / 60);
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, "0")} min`;
}

const quando = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

/** O estado como quem decide lê. `parcial` NÃO vira "concluída": é uma busca
 * que terminou e viu menos do que existia, e a tela existe para mostrar isso. */
const ESTADO: Record<string, string> = {
  ok: "Concluída",
  parcial: "Incompleta",
  falha: "Falhou",
  falhou: "Falhou",
  rodando: "Em andamento",
  cancelada: "Cancelada",
};

/** A cor da bolinha. Estado que não está aqui (cancelada) fica neutro. */
const TOM: Record<string, TomDeEstado> = {
  ok: "ok",
  parcial: "atencao",
  falha: "perigo",
  falhou: "perigo",
  rodando: "andamento",
};

export function ListaDeRodadas({ linhas, comQuem }: { linhas: Rodada[]; comQuem?: boolean }) {
  if (!linhas.length) return <Aviso simples>Nenhuma busca registrada ainda.</Aviso>;
  return (
    <Lista>
      {linhas.map((r) => (
        <ItemDeLista
          key={r.id}
          inicio={
            <Pilula tom={TOM[r.status]} title={`estado: ${r.status}`}>
              {ESTADO[r.status] ?? r.status}
            </Pilula>
          }
          titulo={r.termos ?? "—"}
          meta={
            <>
              <span>#{r.id}</span>
              <span>{quando(r.iniciada_em)}</span>
              <span>{r.finalizada_em ? `durou ${duracao(r.iniciada_em, r.finalizada_em)}` : "em curso"}</span>
              {comQuem && r.usuario && <span>{r.usuario}</span>}
            </>
          }
          fim={
            <dl className={css.rodadaNumeros}>
              <div>
                <dt>vistas no portal</dt>
                <dd>{r.encontradas.toLocaleString("pt-BR")}</dd>
              </div>
              <div title="Tinham alguma das palavras de busca. As outras nunca entram na base.">
                <dt>com nossas palavras</dt>
                <dd>{r.registros_casados.toLocaleString("pt-BR")}</dd>
              </div>
              <div>
                <dt>novas na base</dt>
                <dd data-destaque={r.novas > 0}>{r.novas.toLocaleString("pt-BR")}</dd>
              </div>
            </dl>
          }
        >
          {(r.paginas_perdidas > 0 || r.campos_ausentes > 0 || r.assinatura_mudou) && (
            <span className={css.alertas}>
              {r.paginas_perdidas > 0 && <span>{r.paginas_perdidas} páginas não carregaram</span>}
              {r.campos_ausentes > 0 && (
                <span title="Campo esperado que sumiu do payload. Só o PNCP é instrumentado.">
                  {r.campos_ausentes} campos vieram vazios
                </span>
              )}
              {r.assinatura_mudou && <span>o portal mudou o formato da lista</span>}
            </span>
          )}
          {r.erro && <span className={css.linhaErro} title={r.erro}>{r.erro}</span>}
        </ItemDeLista>
      ))}
    </Lista>
  );
}
